<template>
  <div class="recruit-dash-page">
    <div class="dash-header">
      <div class="dash-header-top">
        <div class="dash-header-left">
          <h1 class="dash-title">外教招聘数据看板</h1>
          <p class="dash-sub">Teacher Recruitment Dashboard / 招聘漏斗 / 今日待办 / 环节流转</p>
        </div>
        <div class="dash-meta">
          <div class="dash-meta-row" v-for="item in metaRows" :key="item.label">
            <span class="dash-meta-label">{{ item.label }}</span>
            <span class="dash-meta-value">{{ item.value }}</span>
          </div>
        </div>
      </div>
      <div class="dash-chips">
        <span class="dash-chip" v-for="chip in chips" :key="chip.label">
          <i class="chip-icon"></i>
          <span class="chip-label">{{ chip.label }}</span>
          <span class="chip-value">{{ chip.value }}</span>
        </span>
      </div>
    </div>

    <div class="filter-bar">
      <span class="filter-label">国籍</span>
      <Input v-model="nationalityInput" placeholder="全部国籍" style="width: 150px"
        @on-enter="applyNationality" @on-blur="applyNationality"></Input>
      <span class="filter-label">渠道</span>
      <Select v-model="filters.regFrom" placeholder="全部渠道" clearable transfer style="width: 160px" @on-change="load">
        <Option v-for="item in channelOptions" :value="item.param" :key="item.id">{{ item.status === 0 ? (item.name + '（停用）') : item.name }}</Option>
      </Select>
      <span class="filter-label">HR</span>
      <Select v-model="filters.hr" placeholder="全部HR" clearable transfer style="width: 140px" @on-change="load">
        <Option v-for="item in $constant.hrList" :value="item.code" :key="item.code">{{ item.text }}</Option>
      </Select>
      <span class="filter-label">时间</span>
      <DatePicker
        :value="dateRange"
        type="daterange"
        format="yyyy-MM-dd"
        :clearable="false"
        transfer
        placeholder="选择日期范围"
        style="width: 230px;"
        @on-change="onDateRangeChange">
      </DatePicker>
      <Button @click="setQuickRange(7)">近7天</Button>
      <Button @click="setQuickRange(30)">近30天</Button>
      <Spin size="small" v-if="loading" class="filter-spin"></Spin>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">招聘漏斗</h2>
        <span class="section-legend">
          <i class="legend-dot is-danger"></i>红=预警
          / <i class="legend-dot is-warning"></i>黄=关注
          / <i class="legend-dot is-healthy"></i>绿=健康
        </span>
      </div>
      <div class="section-filters">
        <span class="filter-item"><i class="legend-dot is-healthy"></i>健康</span>
        <span class="filter-item"><i class="legend-dot is-warning"></i>需关注</span>
        <span class="filter-item"><i class="legend-dot is-danger"></i>预警</span>
        <span class="filter-note">※ 主数字为筛选后人数，右侧为不加筛选的全局人数</span>
      </div>
      <div class="kpi-grid">
        <article v-for="card in kpiCards" :key="card.key" class="kpi-card"
          :class="['is-' + card.level, {'is-clickable': !!card.link}]" @click="openCard(card)">
          <div class="kpi-bar"></div>
          <div class="kpi-body">
            <div class="kpi-title">
              <i class="kpi-dot"></i>
              {{ card.title }}
              <Icon v-if="card.link" type="ios-arrow-right" class="kpi-link"></Icon>
            </div>
            <div class="kpi-value">{{ card.value }}<span class="kpi-unit">人</span></div>
            <div class="kpi-period-row">
              <span class="kpi-period">{{ card.period }}</span>
              <span class="kpi-compare">{{ card.compare }}</span>
            </div>
            <p class="kpi-foot">{{ card.foot }}</p>
          </div>
        </article>
      </div>
    </div>

    <div class="dash-section">
      <div class="section-head">
        <h2 class="section-title">环节流转（按日）</h2>
        <Button class="dash-export" type="primary" :loading="exportLoading" @click="exportTrend">导出</Button>
      </div>
      <p class="section-desc">每天进入各环节的人数，按 tch_recruit_flow 流转记录统计；当前区间 {{ rangeText }}</p>
      <div class="block-card">
        <Table :columns="trendColumns" :data="trendRows" :loading="loading" :height="trendTableHeight"
          :row-class-name="trendRowClass" size="small"></Table>
      </div>
    </div>
  </div>
</template>
<script>
const TREND_KEYS = [
  {key: 'newReg', title: '新增注册'},
  {key: 'invite', title: '邀约'},
  {key: 'interviewDone', title: '面试完成'},
  {key: 'offerSent', title: '发Offer'},
  {key: 'offerAccepted', title: '接Offer'},
  {key: 'toTraining', title: '进入培训'},
  {key: 'opened', title: '开课'},
  {key: 'reserved', title: '转储备'},
  {key: 'eliminated', title: '培训淘汰流失'},
  {key: 'total', title: '合计'}
]

function backlogLevel (n) {
  if (n >= 20) {
    return 'danger'
  }
  if (n > 0) {
    return 'warning'
  }
  return 'healthy'
}

function num (v) {
  let n = Number(v)
  return isFinite(n) ? n : 0
}

export default {
  name: 'TchRecruitDashboard',
  data () {
    let end = this.$moment().format('YYYY-MM-DD')
    let start = this.$moment().subtract(6, 'days').format('YYYY-MM-DD')
    return {
      channelOptions: [],
      nationalityInput: '',
      filters: {
        nationality: '',
        regFrom: '',
        hr: ''
      },
      dateRange: [start, end],
      loading: false,
      reqId: 0,
      updatedAt: '',
      data: {
        counts: {},
        globalCounts: {},
        todayTodo: 0,
        trend: []
      },
      exportLoading: false,
      trendColumns: [{title: '日期', key: 'date', align: 'center', minWidth: 110}].concat(TREND_KEYS.map(function (c) {
        return {
          title: c.title,
          key: c.key,
          align: 'center',
          minWidth: c.key === 'eliminated' ? 120 : 84
        }
      }))
    }
  },
  computed: {
    todayText () {
      return this.$moment().format('YYYY-MM-DD')
    },
    metaRows () {
      return [
        {label: '数据来源', value: 'tch_list / tch_recruit_flow'},
        {label: '口径版本', value: '外教招聘 v1'},
        {label: '最近更新', value: this.updatedAt || '—'}
      ]
    },
    channelName () {
      let code = this.filters.regFrom
      if (!code) {
        return '全部'
      }
      let item = this.channelOptions.find(function (c) {
        return c.param === code
      })
      return item ? item.name : code
    },
    chips () {
      return [
        {label: '国籍', value: this.filters.nationality || '全部'},
        {label: '渠道', value: this.channelName},
        {label: 'HR', value: this.filters.hr || '全部'},
        {label: '时间', value: this.chipRangeText},
        {label: '今日待办', value: num(this.data.todayTodo)}
      ]
    },
    kpiCards () {
      let c = this.data.counts || {}
      let g = this.data.globalCounts || {}
      let period = '截至 ' + this.todayText
      let todo = num(this.data.todayTodo)
      return [
        {
          key: 'todayTodo',
          title: '今日待办',
          value: todo,
          level: backlogLevel(todo),
          period: period,
          compare: '待审核 ' + num(c.pendingReview) + ' + 待面试 ' + num(c.interviewing),
          foot: '今日待办 = 待审核老师 + 待面试老师'
        },
        {
          key: 'pendingReview',
          title: '待审核老师',
          value: num(c.pendingReview),
          level: backlogLevel(num(c.pendingReview)),
          period: period,
          compare: '全局 ' + num(g.pendingReview),
          foot: '注册后还没审核的老师（未审核、未激活）'
        },
        {
          key: 'interviewing',
          title: '待面试老师',
          value: num(c.interviewing),
          level: backlogLevel(num(c.interviewing)),
          period: period,
          compare: '全局 ' + num(g.interviewing),
          foot: '面试中老师，不含已转储备 · 点击进入待面试老师',
          link: '/tch/recruit/pendingInterview'
        },
        {
          key: 'training',
          title: '培训中老师',
          value: num(c.training),
          level: num(c.training) > 0 ? 'healthy' : 'warning',
          period: period,
          compare: '全局 ' + num(g.training),
          foot: '和「培训中老师」列表同口径'
        },
        {
          key: 'probation',
          title: '试用期老师',
          value: num(c.probation),
          level: 'healthy',
          period: period,
          compare: '全局 ' + num(g.probation),
          foot: '类型试用期、状态激活'
        },
        {
          key: 'formal',
          title: '正式老师',
          value: num(c.formal),
          level: 'healthy',
          period: period,
          compare: '全局 ' + num(g.formal),
          foot: '类型正式、状态激活'
        },
        {
          key: 'reserved',
          title: '储备老师',
          value: num(c.reserved),
          level: num(c.reserved) > 0 ? 'warning' : 'healthy',
          period: period,
          compare: '全局 ' + num(g.reserved),
          foot: '已转储备且未归档 · 点击进入储备老师',
          link: '/tch/recruit/reserved'
        },
        {
          key: 'rejected',
          title: '已拒绝老师',
          value: num(c.rejected),
          level: num(c.rejected) > 0 ? 'danger' : 'healthy',
          period: period,
          compare: '全局 ' + num(g.rejected),
          foot: '老师状态为被拒绝'
        }
      ]
    },
    rangeText () {
      let r = this.dateRange || []
      return (r[0] || '—') + ' ~ ' + (r[1] || '—')
    },
    chipRangeText () {
      let r = this.dateRange || []
      if (!r[0] || !r[1]) {
        return '—'
      }
      return this.$moment(r[0]).format('MM-DD') + ' ~ ' + this.$moment(r[1]).format('MM-DD')
    },
    trendRows () {
      return this.withTotal(this.data.trend)
    },
    trendTableHeight () {
      let r = this.dateRange || []
      if (!r[0] || !r[1]) {
        return null
      }
      let days = this.$moment(r[1]).diff(this.$moment(r[0]), 'days') + 1
      return days > 7 ? 460 : null
    }
  },
  created () {
    this.loadRegChannels()
    this.load()
  },
  methods: {
    loadRegChannels () {
      this.$http.get('/tch/reg-channel/options').then((res) => {
        if (res && res.status === 'ok' && res.data) {
          this.channelOptions = res.data
        }
      }).catch(() => {})
    },
    applyNationality () {
      let val = (this.nationalityInput || '').trim()
      if (val === this.filters.nationality) {
        return
      }
      this.filters.nationality = val
      this.load()
    },
    queryBody () {
      let body = {}
      let f = this.filters
      Object.keys(f).forEach(function (k) {
        if (f[k]) {
          body[k] = f[k]
        }
      })
      let r = this.dateRange || []
      body.startDate = r[0]
      body.endDate = r[1]
      return body
    },
    onDateRangeChange (value) {
      let start = value && value[0]
      let end = value && value[1]
      if (!start || !end) {
        this.dateRange = this.dateRange.slice()
        return
      }
      let days = this.$moment(end).diff(this.$moment(start), 'days') + 1
      if (days > 90) {
        this.$Message.warning('时间跨度不能超过90天')
        this.dateRange = this.dateRange.slice()
        return
      }
      this.dateRange = [start, end]
      this.load()
    },
    setQuickRange (days) {
      let end = this.$moment().format('YYYY-MM-DD')
      let start = this.$moment().subtract(days - 1, 'days').format('YYYY-MM-DD')
      this.dateRange = [start, end]
      this.load()
    },
    load () {
      let reqId = ++this.reqId
      this.loading = true
      this.$http.post('/tchRecruit/dashboard', this.queryBody(), {silent: true}).then((res) => {
        if (reqId !== this.reqId) {
          return
        }
        if (res && res.status === 'ok') {
          let d = res.data || {}
          this.data = {
            counts: d.counts || {},
            globalCounts: d.globalCounts || {},
            todayTodo: d.todayTodo,
            trend: d.trend || []
          }
          this.updatedAt = this.$moment().format('YYYY-MM-DD HH:mm')
        } else {
          this.$Message.error((res && res.msg) || '看板加载失败')
        }
      }).catch(() => {}).finally(() => {
        if (reqId === this.reqId) {
          this.loading = false
        }
      })
    },
    withTotal (rows) {
      let list = (rows || []).map(function (r) {
        let row = {date: r.date}
        TREND_KEYS.forEach(function (c) {
          row[c.key] = num(r[c.key])
        })
        return row
      })
      let sum = {date: '合计', isTotal: true}
      TREND_KEYS.forEach(function (c) {
        sum[c.key] = list.reduce(function (a, r) {
          return a + r[c.key]
        }, 0)
      })
      return list.concat([sum])
    },
    trendRowClass (row) {
      return row.isTotal ? 'row-total' : ''
    },
    openCard (card) {
      if (card.link) {
        this.$router.push(card.link)
      }
    },
    exportTrend () {
      let body = this.queryBody()
      let r = this.dateRange || []
      let start = (r[0] || '').replace(/-/g, '')
      let end = (r[1] || '').replace(/-/g, '')
      this.exportLoading = true
      this.$http.post('/tchRecruit/dashboard-trend-export', body, {responseType: 'blob'}).then((response) => {
        const blob = new Blob([response.data], {type: 'application/vnd.ms-excel'})
        const link = document.createElement('a')
        link.href = window.URL.createObjectURL(blob)
        link.download = '外教招聘看板_流转_' + start + '-' + end + '.xlsx'
        link.click()
        this.$Message.success('导出成功')
      }).catch((error) => {
        this.$Message.error('导出失败：' + ((error && error.message) || '未知错误'))
      }).finally(() => {
        this.exportLoading = false
      })
    }
  }
}
</script>
<style scoped lang="less">
@page-bg: #F5F6FA;
@header-bg: #2A3C62;
@header-bg-end: #314E78;
@text: #1A1A1A;
@text-title: #303133;
@text-muted: #8B93A7;
@text-desc: #9AA3B2;
@card: #FFFFFF;
@danger: #E6464E;
@warning: #E89F2D;
@healthy: #0B9E8D;
@radius-header: 16px;
@radius-card: 12px;
@gap: 16px;
@shadow: 0 1px 2px rgba(15, 23, 42, .04), 0 8px 24px rgba(15, 23, 42, .04);

.recruit-dash-page {
  margin: -24px;
  min-height: calc(100vh - 108px);
  padding: 16px;
  background: @page-bg;
}

.dash-header {
  padding: 20px 24px 16px;
  border-radius: @radius-header;
  background: linear-gradient(90deg, @header-bg 0%, @header-bg-end 100%);
  color: #fff;
}

.dash-header-top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: @gap;
}

.dash-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1.3;
  color: #fff;
}

.dash-sub {
  margin: 6px 0 0;
  font-size: 12px;
  color: rgba(255, 255, 255, .7);
}

.dash-meta {
  text-align: right;
  white-space: nowrap;
}

.dash-meta-row {
  font-size: 12px;
  line-height: 1.7;
}

.dash-meta-label {
  margin-right: 8px;
  color: rgba(255, 255, 255, .55);
}

.dash-meta-value {
  color: rgba(255, 255, 255, .92);
}

.dash-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
}

.dash-chip {
  display: inline-flex;
  align-items: center;
  height: 32px;
  padding: 0 12px;
  border: 1px solid rgba(255, 255, 255, .12);
  border-radius: 16px;
  background: rgba(255, 255, 255, .08);
  font-size: 12px;
  color: rgba(255, 255, 255, .88);
}

.chip-icon {
  display: inline-block;
  width: 12px;
  height: 12px;
  margin-right: 6px;
  border: 1px solid rgba(255, 255, 255, .55);
  border-radius: 50%;
}

.chip-label {
  margin-right: 6px;
}

.chip-value {
  font-weight: 600;
}

.filter-bar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: @gap;
  padding: 12px 16px;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
}

.filter-label {
  margin-left: 8px;
  font-size: 13px;
  color: @text-title;
}

.filter-label:first-child {
  margin-left: 0;
}

.filter-spin {
  display: inline-block;
  margin-left: 8px;
}

.dash-section {
  margin-top: 18px;
}

.section-head {
  display: flex;
  align-items: center;
  gap: 12px;
}

.section-title {
  margin: 0;
  padding-left: 10px;
  border-left: 4px solid @header-bg;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  color: @text;
}

.section-legend {
  font-size: 12px;
  color: @text-muted;
}

.section-desc {
  margin: 6px 0 0;
  font-size: 12px;
  color: @text-muted;
}

.dash-export {
  margin-left: auto;
}

.legend-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin: 0 4px 0 8px;
  border-radius: 50%;
  vertical-align: 1px;
}

.legend-dot.is-danger {
  background: @danger;
}

.legend-dot.is-warning {
  background: @warning;
}

.legend-dot.is-healthy {
  background: @healthy;
}

.section-filters {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 10px;
  font-size: 13px;
  color: @text-title;
}

.filter-item {
  display: inline-flex;
  align-items: center;
}

.filter-item .legend-dot {
  margin-left: 0;
  margin-right: 6px;
}

.filter-note {
  margin-left: auto;
  font-size: 12px;
  color: @text-muted;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: @gap;
  margin-top: 16px;
}

.kpi-card {
  display: flex;
  min-height: 200px;
  overflow: hidden;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
}

.kpi-card.is-clickable {
  cursor: pointer;
}

.kpi-card.is-clickable:hover {
  box-shadow: 0 1px 2px rgba(15, 23, 42, .06), 0 10px 28px rgba(15, 23, 42, .10);
}

.kpi-bar {
  flex-shrink: 0;
  width: 4px;
  background: #D8DEE8;
}

.kpi-card.is-danger .kpi-bar,
.kpi-card.is-danger .kpi-dot {
  background: @danger;
}

.kpi-card.is-warning .kpi-bar,
.kpi-card.is-warning .kpi-dot {
  background: @warning;
}

.kpi-card.is-healthy .kpi-bar,
.kpi-card.is-healthy .kpi-dot {
  background: @healthy;
}

.kpi-body {
  flex: 1;
  min-width: 0;
  padding: 16px 18px 14px;
}

.kpi-title {
  display: flex;
  align-items: center;
  font-size: 14px;
  color: @text-title;
}

.kpi-dot {
  display: inline-block;
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  margin-right: 8px;
  border-radius: 50%;
}

.kpi-link {
  margin-left: auto;
  color: @text-desc;
}

.kpi-value {
  margin-top: 18px;
  font-size: 34px;
  font-weight: 700;
  line-height: 1.2;
  color: @text;
}

.kpi-unit {
  margin-left: 4px;
  font-size: 16px;
  font-weight: 600;
}

.kpi-period-row {
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 12px;
  font-size: 12px;
  color: @text-muted;
}

.kpi-compare {
  font-weight: 600;
  color: @text-muted;
  white-space: nowrap;
}

.kpi-foot {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #EEF0F4;
  font-size: 12px;
  line-height: 1.5;
  color: @text-desc;
}

.block-card {
  margin-top: 12px;
  padding: 12px;
  border-radius: @radius-card;
  background: @card;
  box-shadow: @shadow;
}

.block-card /deep/ .ivu-table-wrapper {
  border: 0;
}

.block-card /deep/ .ivu-table:before,
.block-card /deep/ .ivu-table:after {
  display: none;
}

.block-card /deep/ .ivu-table th {
  background: #F7F8FB;
  color: @text-title;
}

.block-card /deep/ .row-total td {
  background: #F2F4F8;
  font-weight: 700;
}

@media (max-width: 1200px) {
  .kpi-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 768px) {
  .recruit-dash-page {
    margin: -16px;
    padding: 12px;
  }

  .dash-header-top {
    flex-direction: column;
  }

  .dash-meta {
    text-align: left;
    white-space: normal;
  }

  .kpi-grid {
    grid-template-columns: 1fr;
  }
}
</style>



// WEBPACK FOOTER //
// TchRecruitDashboard.vue?7f056ebf